import Keycloak from "keycloak-js";

const KEYCLOAK_URL = process.env.REACT_APP_KEYCLOAK_URL || "https://auth.pericopeai.com";
const KEYCLOAK_REALM = process.env.REACT_APP_KEYCLOAK_REALM || "pericope";
const KEYCLOAK_CLIENT_ID = process.env.REACT_APP_KEYCLOAK_CLIENT_ID || "pericope-web";
const AUTH_DISABLED =
  (process.env.REACT_APP_DISABLE_AUTH || "").toLowerCase() === "true";
const DEV_AUTH_ENABLED = AUTH_DISABLED && Boolean(
  (process.env.REACT_APP_DEV_AUTH_SUB || "").trim()
);
const DEV_AUTH_ROLES = String(
  process.env.REACT_APP_DEV_AUTH_ROLES || "default-roles-pericope"
)
  .split(",")
  .map((role) => String(role || "").trim())
  .filter(Boolean);
const DEV_AUTH_PROFILE = {
  sub: (process.env.REACT_APP_DEV_AUTH_SUB || "").trim() || null,
  email: (process.env.REACT_APP_DEV_AUTH_EMAIL || "").trim() || null,
  given_name: (process.env.REACT_APP_DEV_AUTH_GIVEN_NAME || "").trim() || "Dummy",
  family_name: (process.env.REACT_APP_DEV_AUTH_FAMILY_NAME || "").trim() || "Reader",
  name: (process.env.REACT_APP_DEV_AUTH_NAME || "").trim() || "Dummy Reader",
  roles: DEV_AUTH_ROLES,
};

if (AUTH_DISABLED) {
  console.info("[auth] Keycloak disabled via REACT_APP_DISABLE_AUTH");
}

const keycloak = new Keycloak({
  url: KEYCLOAK_URL,
  realm: KEYCLOAK_REALM,
  clientId: KEYCLOAK_CLIENT_ID,
  storage: "localStorage",
});

if (DEV_AUTH_ENABLED) {
  keycloak.authenticated = true;
  keycloak.tokenParsed = {
    sub: DEV_AUTH_PROFILE.sub,
    email: DEV_AUTH_PROFILE.email,
    given_name: DEV_AUTH_PROFILE.given_name,
    family_name: DEV_AUTH_PROFILE.family_name,
    name: DEV_AUTH_PROFILE.name,
    realm_access: { roles: DEV_AUTH_PROFILE.roles },
  };
}

let refreshTimer = null;
let initPromise = null;
const authListeners = new Set();

const isBrowser = typeof window !== "undefined";
const DEBUG_EVENT_LIMIT = 30;
const EVENTS_STORAGE_KEY = "pericope:keycloakDebugEvents";
const LAST_LOGIN_STORAGE_KEY = "pericope:keycloakLastLogin";
let debugEventBuffer = [];

function readEventsFromStorage() {
  if (!isBrowser) {
    return;
  }
  try {
    const raw = sessionStorage.getItem(EVENTS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        debugEventBuffer = parsed.slice(-DEBUG_EVENT_LIMIT);
      }
    }
  } catch (err) {
    console.warn("[auth][debug] Failed to read debug events from storage", err);
    debugEventBuffer = [];
  }
}

function persistEventsToStorage() {
  if (!isBrowser) {
    return;
  }
  try {
    sessionStorage.setItem(EVENTS_STORAGE_KEY, JSON.stringify(debugEventBuffer));
  } catch (err) {
    console.warn("[auth][debug] Failed to persist debug events", err);
  }
}

function emitClientFailure(payload) {
  if (!isBrowser) {
    return;
  }
  if (typeof window.pericopeReportClientFailure === "function") {
    window.pericopeReportClientFailure(payload || {});
    return;
  }
  try {
    window.dispatchEvent(new CustomEvent("pericope:client-failure", {
      detail: payload || {},
    }));
  } catch (err) {
    console.warn("[auth][debug] Failed to emit client failure event", err);
  }
}

function readLastLoginFromStorage() {
  if (!isBrowser) {
    return null;
  }
  try {
    const raw = sessionStorage.getItem(LAST_LOGIN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (err) {
    console.warn("[auth][debug] Failed to read last login request", err);
    return null;
  }
}

if (isBrowser) {
  readEventsFromStorage();
}

function safeClone(value) {
  if (value == null) {
    return value;
  }
  try {
    return JSON.parse(JSON.stringify(value));
  } catch {
    return value;
  }
}

function snapshotLocation() {
  if (!isBrowser) {
    return {};
  }
  return {
    href: window.location.href,
    search: window.location.search,
    hash: window.location.hash,
  };
}

function updateDebugInfo(extra = {}) {
  if (!isBrowser) {
    return;
  }
  const current = window.pericopeKeycloakDebug || {};
  const lastLoginRequest = readLastLoginFromStorage() || current.lastLoginRequest || null;
  window.pericopeKeycloakDebug = {
    ...current,
    lastUpdated: new Date().toISOString(),
    location: snapshotLocation(),
    lastLoginRequest,
    ...extra,
  };
}

function collectAuthParams() {
  if (!isBrowser) {
    return {};
  }
  const search = new URLSearchParams(window.location.search || "");
  const hashString = window.location.hash?.startsWith("#")
    ? window.location.hash.slice(1)
    : window.location.hash || "";
  const hash = new URLSearchParams(hashString || "");
  const trackedKeys = ["code", "state", "session_state", "error", "error_description"];
  const result = {};
  trackedKeys.forEach((key) => {
    if (search.has(key)) {
      result[`query_${key}`] = search.get(key);
    }
    if (hash.has(key)) {
      result[`hash_${key}`] = hash.get(key);
    }
  });
  return result;
}

function recordDebugEvent(event, extra = {}) {
  if (!isBrowser) {
    return;
  }
  const payload = {
    event,
    timestamp: new Date().toISOString(),
    location: snapshotLocation(),
    authParams: collectAuthParams(),
    referrer: document.referrer || null,
    ...safeClone(extra),
  };
  debugEventBuffer.push(payload);
  if (debugEventBuffer.length > DEBUG_EVENT_LIMIT) {
    debugEventBuffer.shift();
  }
  const current = window.pericopeKeycloakDebug || {};
  const lastLoginRequest = readLastLoginFromStorage() || current.lastLoginRequest || null;
  window.pericopeKeycloakDebug = {
    ...current,
    events: [...debugEventBuffer],
    lastEvent: payload,
    lastUpdated: payload.timestamp,
    lastLoginRequest,
  };
  persistEventsToStorage();
  console.debug(`[auth][debug] ${event}`, payload);
}

function removeAuthParamsFromUrl() {
  if (!isBrowser) {
    return;
  }
  try {
    const url = new URL(window.location.href);
    const hadQueryParams =
      url.searchParams.has("code") ||
      url.searchParams.has("state") ||
      url.searchParams.has("session_state");
    const hadHashParams =
      url.hash && /[?&#](code|state|session_state)=/.test(url.hash);
    if (!hadQueryParams && !hadHashParams) {
      return;
    }
    url.searchParams.delete("code");
    url.searchParams.delete("state");
    url.searchParams.delete("session_state");
    if (hadHashParams) {
      const cleanHash = url.hash
        .replace(/[#&]?code=[^&]*/g, "")
        .replace(/[#&]?state=[^&]*/g, "")
        .replace(/[#&]?session_state=[^&]*/g, "")
        .replace(/&&+/g, "&")
        .replace(/[#&]$/, "");
      url.hash = cleanHash.startsWith("#") ? cleanHash : cleanHash ? `#${cleanHash}` : "";
    }
    window.history.replaceState(null, document.title, `${url.pathname}${url.search}${url.hash}`);
  } catch (err) {
    console.warn("[auth] Failed to remove auth params from URL", err);
  }
}

function clearRefreshTimer() {
  if (refreshTimer) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

function scheduleTokenRefresh() {
  clearRefreshTimer();
  if (!keycloak?.tokenParsed?.exp) {
    return;
  }

  const expiresAt = keycloak.tokenParsed.exp * 1000;
  const now = Date.now();
  const leadTime = 60 * 1000;
  const timeout = Math.max(expiresAt - now - leadTime, 5 * 1000);

  refreshTimer = window.setTimeout(async () => {
    try {
      await keycloak.updateToken(60);
      scheduleTokenRefresh();
    } catch (err) {
      console.warn("[auth] token refresh failed; forcing re-login", err);
      login({ prompt: "login" });
    }
  }, timeout);
}

function notifyAuthListeners(authenticated) {
  authListeners.forEach((listener) => {
    try {
      listener(authenticated);
    } catch (err) {
      console.warn("[auth] Auth listener failed", err);
    }
  });
}

keycloak.onAuthSuccess = () => {
  recordDebugEvent("keycloak:onAuthSuccess", {
    tokenExpiresAt: keycloak?.tokenParsed?.exp || null,
  });
  scheduleTokenRefresh();
  notifyAuthListeners(true);
};
keycloak.onAuthRefreshSuccess = () => {
  recordDebugEvent("keycloak:onAuthRefreshSuccess", {
    tokenExpiresAt: keycloak?.tokenParsed?.exp || null,
  });
  scheduleTokenRefresh();
  notifyAuthListeners(true);
};
keycloak.onTokenExpired = () => {
  recordDebugEvent("keycloak:onTokenExpired");
  keycloak
    .updateToken(30)
    .then(scheduleTokenRefresh)
    .catch((err) => {
      console.warn("[auth] token refresh on expiry failed; redirecting to login", err);
      login({ prompt: "login" });
    });
};
keycloak.onAuthLogout = () => {
  clearRefreshTimer();
  recordDebugEvent("keycloak:onAuthLogout");
  notifyAuthListeners(false);
};

if (isBrowser) {
  window.addEventListener("unhandledrejection", (event) => {
    if (event?.reason?.message?.includes("can only be initialized once")) {
      console.warn("[auth] Duplicate Keycloak init attempt ignored", event.reason);
      event.preventDefault?.();
    }
  });
}

export function initKeycloak(options = {}) {
  if (AUTH_DISABLED) {
    initPromise = Promise.resolve(DEV_AUTH_ENABLED);
    notifyAuthListeners(DEV_AUTH_ENABLED);
    return initPromise;
  }
  if (initPromise) {
    return initPromise;
  }

  const hasAuthParams =
    isBrowser &&
    ((window.location.search && /[?&](code|session_state)=/.test(window.location.search)) ||
      (window.location.hash && /[#&](code|session_state)=/.test(window.location.hash)));

  const onLoadMode = "check-sso";

  const initOptions = {
    onLoad: onLoadMode,
    pkceMethod: "S256",
    responseMode: "query",
    flow: "standard",
    checkLoginIframe: false,
    silentCheckSsoFallback: false,
    messageReceiveTimeout: 2000,
    thirdPartyCookies: false,
    ...(isBrowser
      ? {
          silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
          redirectUri: `${window.location.origin}/`,
        }
      : {}),
    ...(options || {}),
  };

  if (isBrowser) {
    const debugInitOptions = safeClone(initOptions);
    updateDebugInfo({ initOptions: debugInitOptions });
    recordDebugEvent("init:start", { initOptions: debugInitOptions });
  }

  initPromise = keycloak
    .init(initOptions)
    .then((authenticated) => {
      const hasToken = Boolean(keycloak?.token);
      const effectiveAuthenticated = authenticated || hasToken;
      recordDebugEvent("init:resolved", {
        authenticated: effectiveAuthenticated,
        reportedAuthenticated: authenticated,
        hasToken,
        tokenExpiresAt: keycloak?.tokenParsed?.exp || null,
      });
      if (effectiveAuthenticated) {
        scheduleTokenRefresh();
        if (hasAuthParams) {
          removeAuthParamsFromUrl();
        }
      } else {
        clearRefreshTimer();
      }
      if (isBrowser) {
        window.pericopeKeycloak = keycloak;
      }
      return effectiveAuthenticated;
    })
    .then((authenticated) => {
      recordDebugEvent("init:notifyAuthListeners", { authenticated });
      notifyAuthListeners(authenticated);
      return authenticated;
    })
    .catch((err) => {
      const hasToken = Boolean(keycloak?.token || keycloak?.tokenParsed);
      const errorMessage = typeof err === "string" ? err : err?.error || err?.message || "";

      const loginRequired =
        !err ||
        err === "login_required" ||
        errorMessage === "login_required" ||
        errorMessage === "Not logged in" ||
        (typeof err === "object" && err?.error_description === "Not logged in");

      const thirdPartyTimeout =
        errorMessage === "Timeout when waiting for 3rd party check iframe message." ||
        errorMessage === "3p_cookie_required" ||
        (typeof err === "string" && err.includes("3rd party check iframe message"));

      if (hasToken) {
        console.info("[auth] Keycloak init reported error but token is present; treating as authenticated");
        scheduleTokenRefresh();
        if (isBrowser) {
          window.pericopeKeycloak = keycloak;
          window.pericopeKeycloakError = null;
          updateDebugInfo({
            lastInitError: {
              message: errorMessage || err || "unknown_error_with_token",
              location: snapshotLocation(),
              recoveredWithToken: true,
            },
          });
        }
        notifyAuthListeners(true);
        return true;
      }

      if (loginRequired) {
        const context = {
          message: errorMessage || err || "login_required",
          hasToken,
          location: snapshotLocation(),
        };
        console.info("[auth] Keycloak check-sso found no active session", context);
        recordDebugEvent("init:login_required", context);
        clearRefreshTimer();
        if (isBrowser) {
          window.pericopeKeycloak = keycloak;
          window.pericopeKeycloakError = null;
          updateDebugInfo({ lastInitError: context });
        }
        notifyAuthListeners(false);
        return false;
      }

      if (thirdPartyTimeout) {
        const context = {
          message: errorMessage || "third_party_timeout",
          location: snapshotLocation(),
        };
        console.info("[auth] Keycloak third-party cookie check timed out; treating as anonymous session", context);
        recordDebugEvent("init:third_party_timeout", context);
        emitClientFailure({
          kind: "keycloak_third_party_cookie_timeout",
          phase: "auth_bootstrap",
          severity: "warning",
          message: context.message,
          route: context.location?.pathname || "",
        });
        clearRefreshTimer();
        if (isBrowser) {
          window.pericopeKeycloak = keycloak;
          window.pericopeKeycloakError = null;
          updateDebugInfo({ lastInitError: context });
        }
        notifyAuthListeners(false);
        return false;
      }

      const errorContext = {
        message: err?.message || err?.error || String(err),
        stack: err?.stack || null,
        hasToken,
        location: snapshotLocation(),
      };
      console.error("[auth] Keycloak init failed", err, snapshotLocation());
      recordDebugEvent("init:error", errorContext);
      emitClientFailure({
        kind: "keycloak_init_failed",
        phase: "auth_bootstrap",
        severity: "warning",
        message: errorContext.message,
        stack: errorContext.stack || "",
        route: errorContext.location?.pathname || "",
      });
      clearRefreshTimer();
      if (isBrowser) {
        window.pericopeKeycloak = keycloak;
        window.pericopeKeycloakError = err || null;
        updateDebugInfo({
          lastInitError: {
            message: err?.message || err?.error || String(err),
            error: err,
            location: snapshotLocation(),
            hasToken,
          },
        });
      }
      notifyAuthListeners(false);
      initPromise = null;
      throw err;
    });

  return initPromise;
}

export function login(options = {}) {
  if (AUTH_DISABLED) {
    notifyAuthListeners(DEV_AUTH_ENABLED);
    return Promise.resolve(DEV_AUTH_ENABLED);
  }
  const redirectUri = isBrowser ? `${window.location.origin}/` : undefined;
  if (isBrowser) {
    const clonedOptions = safeClone(options || {});
    const payload = {
      redirectUri,
      options: clonedOptions,
      hasOptions: Boolean(clonedOptions && Object.keys(clonedOptions).length),
    };
    updateDebugInfo({
      lastLoginRequest: {
        ...payload,
        timestamp: new Date().toISOString(),
      },
    });
    try {
      sessionStorage.setItem(
        LAST_LOGIN_STORAGE_KEY,
        JSON.stringify({ ...payload, timestamp: new Date().toISOString() })
      );
    } catch (err) {
      console.warn("[auth][debug] Failed to store last login request", err);
    }
    recordDebugEvent("login:triggered", payload);
    console.info("[auth] Triggering Keycloak login", payload);
  }
  return keycloak.login({ redirectUri, ...(options || {}) });
}

export async function logout(options = {}) {
  if (AUTH_DISABLED) {
    if (DEV_AUTH_ENABLED) {
      keycloak.authenticated = false;
    }
    notifyAuthListeners(false);
    return;
  }
  clearRefreshTimer();
  try {
    const redirectUri = isBrowser ? `${window.location.origin}/` : undefined;
    recordDebugEvent("logout:triggered", { redirectUri });
    await keycloak.logout({ redirectUri, ...(options || {}) });
  } finally {
    keycloak.clearToken();
    initPromise = null;
  }
}

export async function getAccessToken(minValiditySeconds = 30) {
  if (AUTH_DISABLED) {
    return null;
  }
  if (!keycloak.authenticated) {
    return null;
  }
  try {
    await keycloak.updateToken(minValiditySeconds);
    scheduleTokenRefresh();
    return keycloak.token || null;
  } catch (err) {
    console.error("[auth] Failed to refresh access token", err);
    return null;
  }
}

export function subscribeToAuth(listener) {
  if (typeof listener === "function") {
    authListeners.add(listener);
    return () => authListeners.delete(listener);
  }
  return () => {};
}

export function getSubject() {
  if (AUTH_DISABLED) return DEV_AUTH_PROFILE.sub;
  return keycloak?.tokenParsed?.sub || null;
}

export function getTokenProfile() {
  if (AUTH_DISABLED) {
    return DEV_AUTH_ENABLED ? DEV_AUTH_PROFILE : {
      sub: null,
      email: null,
      given_name: null,
      family_name: null,
      name: null,
      roles: [],
    };
  }
  const parsed = keycloak?.tokenParsed || {};
  return {
    sub: parsed.sub || null,
    email: parsed.email || parsed.preferred_username || null,
    given_name: parsed.given_name || null,
    family_name: parsed.family_name || null,
    name: parsed.name || null,
    roles: Array.isArray(parsed?.realm_access?.roles) ? parsed.realm_access.roles : [],
  };
}

export { keycloak, AUTH_DISABLED, DEV_AUTH_ENABLED };
